// Quick guide — Advanced Word Order (A2). The basic guide covers Subject-Verb-
// Object; this one adds the pieces that trip learners up once sentences grow:
// where adjectives sit, how object pronouns jump in front of the verb, the
// indirect-before-direct order, and the gli/le → glielo fusion. Hand-drawn paper
// style with audio on every example.
import { speakButtonHTML } from "../lib/audio.js";

// One worked example: the Italian (with the moving part in <b>), an English
// gloss, and an optional note. Audio reads the plain Italian.
function ex(es, en, note) {
  const plain = es.replace(/<[^>]+>/g, "");
  return `
    <div class="mt-2">
      <p class="text-sage-800">${es}${speakButtonHTML(plain)} <span class="text-base text-sage-500" style="font-family:Inter,sans-serif">— ${en}</span></p>
      ${note ? `<p class="text-sm text-sage-500" style="font-family:Inter,sans-serif">${note}</p>` : ""}
    </div>`;
}

export function advancedWordOrderView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Advanced Word Order 🪡</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Once your sentences carry an object, a pronoun, or a description, Italian puts the pieces in places English doesn't. These are the patterns that matter most at A2.</p>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg space-y-5">

        <div>
          <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · Adjectives go after the noun</h3>
          <p class="text-sage-700 mt-1">The default is <b>noun + adjective</b> — the opposite of English — and the adjective agrees in gender and number.</p>
          ${ex("un libro <b>rosso</b>", "a red book")}
          ${ex("le case <b>bianche</b>", "the white houses", "Plural noun → plural adjective.")}
          <p class="text-sage-700 mt-3">A few short, common ones go <i>before</i>: <b>bello, buono, bravo, grande, brutto, giovane</b>, and numbers / quantities.</p>
          ${ex("un <b>buon</b> amico · <b>molti</b> libri", "a good friend · many books")}
          <p class="text-sage-700 mt-3">And a handful change meaning by position:</p>
          <div class="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-base">
            <span><b>un grande uomo</b> a great man · <b>un uomo grande</b> a big man</span>
            <span><b>un vecchio amico</b> a longtime friend · <b>un amico vecchio</b> an elderly friend</span>
          </div>
        </div>

        <div>
          <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · Object pronouns jump in front of the verb</h3>
          <p class="text-sage-700 mt-1">Unlike English, the object pronoun (mi, ti, lo, la, ci, vi, li, le, gli) sits <b>before</b> the conjugated verb.</p>
          ${ex("<b>Lo</b> vedo.", "I see it / him.", "Not “vedo lo”.")}
          ${ex("Maria <b>mi</b> chiama.", "María calls me.")}
          <p class="text-sage-700 mt-3">With an infinitive or an -ing form, you may instead <b>attach it to the end</b> — both are correct:</p>
          ${ex("<b>Ti</b> voglio vedere. = Voglio veder<b>ti</b>.", "I want to see you.", "The infinitive drops its final -e: vedere → vederti.")}
          ${ex("<b>Lo</b> sto facendo. = Sto facendo<b>lo</b>.", "I'm doing it.")}
        </div>

        <div>
          <h3 class="text-2xl text-sky-700 sketch-underline inline-block">3 · Two pronouns: indirect before direct</h3>
          <p class="text-sage-700 mt-1">When you have both — <i>to whom</i> and <i>what</i> — the order is <b>indirect + direct + verb</b>, and <i>mi/ti/ci/vi</i> shift to <b>me/te/ce/ve</b>.</p>
          ${ex("Lei <b>me lo</b> dà.", "She gives it to me.", "me = to me (indirect) · lo = it (direct).")}
          ${ex("<b>Te li</b> mando?", "Shall I send them to you?")}
        </div>

        <div>
          <h3 class="text-2xl text-rose-600 sketch-underline inline-block">4 · gli / le + lo fuse into glielo</h3>
          <p class="text-sage-700 mt-1">When the indirect <b>gli</b> (to him/them) or <b>le</b> (to her) meets a direct <b>lo/la/li/le</b>, they merge into one word starting <b>glie-</b>.</p>
          ${ex("gli + lo → <b>glielo</b> dico", "I tell it to him", "One word: glielo, gliela, glieli, gliele.")}
          ${ex("<b>Gliele</b> ho date.", "I gave them to them/her.", "glie- covers to him, to her, to them — add “a lui / a lei / a loro” to be clear.")}
        </div>

        <div>
          <h3 class="text-2xl text-violet-700 sketch-underline inline-block">5 · Where “non” and adverbs sit</h3>
          <p class="text-sage-700 mt-1"><b>Non</b> (and any pronouns) come as a block right before the verb.</p>
          ${ex("<b>Non</b> me lo dà.", "He doesn't give it to me.", "non → pronouns → verb, all together.")}
          ${ex("Arrivo <b>sempre</b> tardi.", "I always arrive late.", "Frequency adverbs (sempre, mai, spesso) usually sit right after the verb.")}
        </div>

      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Rule of thumb: pronouns cling to the verb (in front of a conjugated one, or stuck on the end of an infinitive / -ing form), and “to-whom” always comes before “what”.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
